import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Crown } from "lucide-react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Button } from "@/components/ui/button";
import { useApi, errMsg, fmtDate } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { Stat } from "@/components/dashboard/Shared";

const tip = { contentStyle: { background: "#181824", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" } };

export function ProStatus({ me }) {
  const api = useApi();
  const { refresh } = useMe();
  if (!me.is_pro) {
    return (
      <div data-testid="pro-upsell" className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-amber-500/30 bg-[#1b1610] p-5">
        <p className="flex items-center gap-2 text-sm text-amber-200"><Crown className="h-4 w-4" />Con el Plan Pro recibes créditos cada mes, estadísticas detalladas y revisión prioritaria.</p>
        <Link to="/creditos" data-testid="pro-upsell-link" className="rounded-full bg-amber-400 px-4 py-2 text-sm font-semibold text-black">Ver Plan Pro</Link>
      </div>
    );
  }
  const act = async (path, ok) => { try { await api("post", path); toast.success(ok); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div data-testid="pro-status" className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-amber-500/30 bg-[#1b1610] p-5">
      <p className="flex items-center gap-2 text-sm text-amber-200">
        <Crown className="h-4 w-4" />Plan {me.pro_plan === "pro_monthly_15" ? "Pro Plus" : "Pro"} activo ·{" "}
        {me.pro_cancel_at_period_end ? `termina el ${fmtDate(me.pro_until)}` : `se renueva el ${fmtDate(me.pro_until)}`}
      </p>
      {me.pro_cancel_at_period_end ? (
        <Button size="sm" data-testid="pro-resume-btn" onClick={() => act("/pro/resume", "Suscripción reactivada")} className="rounded-full bg-amber-400 text-black hover:bg-amber-300">Reactivar</Button>
      ) : (
        <Button size="sm" variant="outline" data-testid="pro-cancel-btn" onClick={() => window.confirm("¿Cancelar tu Plan Pro al final del período?") && act("/pro/cancel", "Tu plan seguirá activo hasta el fin del período")} className="rounded-full border-white/20 bg-transparent text-slate-200 hover:bg-white/10">Cancelar plan</Button>
      )}
    </div>
  );
}

export function ProStats({ me }) {
  const api = useApi();
  const [s, setS] = useState(null);
  const allowed = me.is_pro || me.role === "admin";
  useEffect(() => { if (allowed) api("get", "/me/stats").then(setS).catch(() => {}); }, [api, allowed]);
  if (!allowed) return <ProStatus me={me} />;
  if (!s) return null;
  const daily = s.daily.map((d) => ({ ...d, label: d.day.slice(5).split("-").reverse().join("/") }));
  return (
    <div data-testid="pro-stats" className="space-y-8">
      <div className="grid gap-4 sm:grid-cols-3">
        <Stat id="plays-7d" label="Reproducciones 7 días" value={s.plays_7d} accent />
        <Stat id="plays-30d" label="Reproducciones 30 días" value={s.plays_30d} />
        <Stat id="likes-total" label="Me gusta" value={s.tracks.reduce((a, t) => a + (t.likes || 0), 0)} />
      </div>
      <div className="rounded-xl border border-white/10 bg-[#12121A] p-6">
        <p className="font-heading text-lg font-bold text-white">Reproducciones por día</p>
        <div className="mt-4 h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={daily}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="label" stroke="#64748b" fontSize={11} interval={4} />
              <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} width={30} />
              <Tooltip {...tip} />
              <Area type="monotone" dataKey="plays" name="Reproducciones" stroke="#FF4500" fill="#FF4500" fillOpacity={0.2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-white/10 bg-[#12121A] p-6">
          <p className="font-heading text-lg font-bold text-white">Por canal</p>
          <div className="mt-4 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={s.channels}>
                <XAxis dataKey="channel" stroke="#64748b" fontSize={11} />
                <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} width={30} />
                <Tooltip {...tip} />
                <Bar dataKey="plays" name="Reproducciones" fill="#FFB020" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div data-testid="pro-stats-tracks" className="rounded-xl border border-white/10 bg-[#12121A] p-6">
          <p className="font-heading text-lg font-bold text-white">Por canción</p>
          <div className="mt-4 space-y-3">
            {s.tracks.map((t) => (
              <div key={t.id} className="flex items-center justify-between border-b border-white/5 pb-2 text-sm">
                <span className="truncate text-white">{t.title}</span>
                <span className="shrink-0 font-mono text-xs text-slate-400">{t.plays} total · {t.plays_7d} esta semana · {t.likes} ♥</span>
              </div>
            ))}
            {!s.tracks.length && <p className="text-sm text-slate-500">Todavía no tienes canciones.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
